import {nextTick} from 'vue';
import createVueApp from '../shared/vue/create-app';
import VueMultiselectExtended from './inputs/multiselect';
import axios from 'axios';
import {globalActions} from '../store/global-actions';
import initCustomControls from './custom-controls';
import {store} from '../store/global-store';
import {capture, LogLevel, OutputType} from 'html-screen-capture-js';
import _u from '../shared/utils';
import {configure, defineRule, Field, Form} from 'vee-validate';
import {email, required} from '@vee-validate/rules';
import localizedValidationMessages from '../shared/validation-localization/localization';

defineRule('required', required);
defineRule('email', email);

const initFeedback = (settings) => {
    configure({
        generateMessage: localizedValidationMessages(),
    });

    const app = createVueApp({ // NOSONAR
        data() {
            return {
                feedbackTypes: settings.feedbackTypes,
                loading: false,
                openFeedbackModal: false,
                selectedType: null,
                selectedTypeValue: 0,
                description: null,
                needScreenshot: true,
                userAgent: navigator.userAgent,
                userAgentSize: `${window.innerWidth} x ${window.innerHeight}`,
                resolution: `${window.screen.width} x ${window.screen.height}`,
                referencedUserEmail: null,
                feedbackButtonVisibility: true,
            };
        },
        components: {
            Multiselect: VueMultiselectExtended,
            ValidationForm: Form,
            ValidationField: Field,
        },
        computed: {
            selectedFeedbackType() {
                return this.selectedType ? this.selectedType.value : 0;
            },
            feedbackModalOpened: {
                get() {
                    return this.openFeedbackModal && !this.loading;
                },
                set(newValue) {
                    this.openFeedbackModal = newValue;
                },
            },
            validationModel() {
                return this.description || {};
            },
        },
        methods: {
            openFeedback() {
                this.openFeedbackModal = true;
            },

            submitFeedbackForm(values) {
                const antiforgeryToken = this.$refs.antiforgeryToken.value;

                this.loading = true;

                this.makeScreenshot()
                    .then((image) => this.sendFeedback(values, image, antiforgeryToken))
                    .catch(this.showFeedbackError);
            },

            sendFeedback(feedbackFormData, image, antiforgeryToken) {
                if (feedbackFormData.type !== this.selectedFeedbackType) {
                    feedbackFormData.type = feedbackFormData.type.value;
                }

                if (image) {
                    feedbackFormData.Screenshot = image;
                }

                const requestConfig = _u.createRequestConfig(antiforgeryToken);

                axios.post(settings.submitUrl, feedbackFormData, requestConfig)
                    .then((response) => {
                        this.clearFields();
                        globalActions.openInfoModal(settings.labels.sendHeader, settings.labels.sendBody);
                    })
                    .catch(this.showFeedbackError);
            },

            showFeedbackError() {
                this.loading = false;
                this.openFeedbackModal = false;
                globalActions.openInfoModal(settings.labels.errorHeader, settings.labels.errorMessage);
            },

            clearFields() {
                Object.assign(this.$data, this.$options.data.apply(this));
            },

            makeScreenshot() {
                return new Promise((resolve) => {
                    if (!this.needScreenshot) {
                        resolve(null);
                        return;
                    }

                    setTimeout(() => {
                        const img = capture(OutputType.STRING, document, {
                            tagsOfIgnoredDocBodyElements: ['script', 'iframe', 'svg', 'template', 'meta', 'link', 'noscript'], // attrKeyValuePairsOfIgnoredElements : { 'data-test-container': 'map'},
                            tagsOfSkippedElementsForChildTreeCssHandling: ['iframe', 'svg', 'template'],
                            classesOfIgnoredDocBodyElements: ['feedback', 'fa', 'multiselect__content-wrapper'],
                            logLevel: LogLevel.ERROR,
                        });
                        resolve(img);
                    },
                    500);

                    setTimeout(() => {
                        resolve(null);
                    },
                    10000); // resolve anyway if too long
                });
            },

            _subscribeOpenFeedback() {
                store.subscribe((mutation) => {
                    if (mutation.type === 'openFeedback') {
                        nextTick(() => {
                            if (mutation.payload && mutation.payload.type != null) {
                                this.selectedType = this.feedbackTypes[mutation.payload.type];
                            }
                            this.openFeedback();
                        });
                    }
                });
            },

            _subscribeShowFeedbackButton() {
                store.subscribe((mutation) => {
                    if (mutation.type === 'showFeedbackButton') {
                        this.feedbackButtonVisibility = true;
                    }
                });
            },

            _subscribeHideFeedbackButton() {
                store.subscribe((mutation) => {
                    if (mutation.type === 'hideFeedbackButton') {
                        this.feedbackButtonVisibility = false;
                    }
                });
            },
        },

        mounted() {
            this._subscribeOpenFeedback();
            this._subscribeShowFeedbackButton();
            this._subscribeHideFeedbackButton();
            this.referencedUserEmail = localStorage.getItem('referencedUserEmail');
        },
    });

    initCustomControls(app);
    app.mount(settings.elSelector);
};

export default initFeedback;
